import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { FPAData, FPADataFactory, FPADataTypes } from '../../../../infra/protected/FPA/FPAData';
import { ListProjectItemRequest } from '../../../../services/soap/item/requests/ListProjectItemRequest';
import { ProjectItem } from '../../../../services/soap/item/requests/ProjectItem';
import { ListProjectItemRequestOptions_ProjectItem } from '../../../../services/soap/item/requests/ListProjectItemRequestOptions';
import { CreateProjectItemRequest } from '../../../../services/soap/item/requests/CreateProjectItemRequest';
import { ListActivityTemplateRequest } from '../../../../services/soap/project/requests/ListActivityTemplateRequest';
import { CreateActivityRequest } from '../../../../services/soap/project/requests/CreateActivityRequest';
import { Activity } from '../../../../services/soap/dataObjects/Activity';
import { GetContextPathRequest } from '../../../../services/soap/project/requests/GetContextPathRequest';
import { ToastQueue } from '@react-spectrum/toast';
import { useTranslation } from 'react-i18next';
import { Key } from '@adobe/react-spectrum';
import { GetActivityTemplateRequest } from '../../../../services/soap/project/requests/GetActivityTemplateRequest';
import { GetFolderProjectTypeRequest } from '../../../../services/soap/project/requests/GetFolderProjectTypeRequest';
import { ActivityTemplate as SubActivityTemplate } from '../../../../services/soap/project/responses/GetFolderProjectTypeResponse';
import { ActivityTemplate } from '../../../../services/soap/project/responses/GetActivityTemplateResponse';
import { DateValue } from '@internationalized/date';

export interface IUseAddMultipleActivitiesDialogProps {
  masterActivity: FPAData;
  onClose: (reload: boolean) => void;
  selectedContext?: string;
}

export function useAddMultipleActivitiesDialog({ masterActivity, onClose, selectedContext = '0' }: IUseAddMultipleActivitiesDialogProps) {
  const { store, projectService, itemService } = useDependency();
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [dateFrom, setDateFrom] = useState<DateValue | null>(null);
  const [dateTo, setDateTo] = useState<DateValue | null>(null);
  const [servicedItemsList, setServicedItemsList] = useState<ProjectItem[]>([]);
  const [selectedServicedItemKeys, setSelectedServicedItemKeys] = useState<Set<Key>>(new Set());
  const [activityTemplates, setActivityTemplates] = useState<SubActivityTemplate[]>([]);
  const [selectedActivityType, setSelectedActivityType] = useState<number | null>(null);
  const [initialStatus, setInitialStatus] = useState<string | undefined>(undefined);
  const [selectedActivityTemplateFormId, setSelectedActivityTemplateFormId] = useState<string | undefined>(undefined);
  const [masterActivityContext, setMasterActivityContext] = useState<FPAData[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');

  const _t = (key: string, params?: Record<string, any>) => t(key, { ns: 'layout_components', ...params });
  
  // Debounce search query with 400ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load serviced items from all customer projects
  const loadServicedItems = useCallback(async () => {
    setLoading(true);
    try {
      let requestOptions: any = {};
      
      // If context is selected and not 'All' (0), use context-specific filtering
      if (selectedContext && selectedContext !== '0' && masterActivityContext.length > 0) {
        const selectedContextData = masterActivityContext.find(ctx => +ctx.id === +selectedContext);
        if (selectedContextData) {
          // Use ListProjectItemRequestOptions_ProjectItem for context-specific filtering
          // Using id as both folder and project since Context2 doesn't have separate folder/project properties
          const options = new ListProjectItemRequestOptions_ProjectItem(
            selectedContextData.type === FPADataTypes.FOLDER ? selectedContextData.id.toString() : '',
            selectedContextData.type === FPADataTypes.PROJECT ? selectedContextData.id.toString() : '',
            selectedContextData.type === FPADataTypes.ACTIVITY ? selectedContextData.id.toString() : '',
            '3' // project_type for items
          );
          requestOptions = options;
        }
      }
      
      const response = await itemService.listProjectItem(
        new ListProjectItemRequest(store.Server, store.SessionId, 10, 0, requestOptions)
      );
      
      if (response.result === 'OK' && response.ROWS && response.ROWS.length > 0) {
        const items = response.ROWS.map(row => row.ITEM);
        setServicedItemsList(items);
      } else {
        setServicedItemsList([]);
      }
    } catch (error) {
      console.error('Error loading serviced items:', error);
      ToastQueue.negative(t('error_loading_serviced_items', { ns: 'layout_components' }), { timeout: 3000 });
      setServicedItemsList([]);
    } finally {
      setLoading(false);
    }
  }, [itemService, store.Server, store.SessionId, selectedContext, masterActivityContext, t]);

  // Fetch specific activity template by ID
  const fetchActivityTemplateById = useCallback(async (templateId: number) => {
    const response = await projectService.getActivityTemplate(
      new GetActivityTemplateRequest(store.Server, store.SessionId, templateId)
    );
    return response;
  }, [projectService, store.Server, store.SessionId]);

  // Fetch folder project type and its activity templates
  const fetchFolderProjectType = useCallback(async (activityTemplateType: number) => {
    const response = await projectService.getFolderProjectType(
      new GetFolderProjectTypeRequest(store.Server, store.SessionId, activityTemplateType)
    );
    return response;
  }, [projectService, store.Server, store.SessionId]);

  // Load activity templates
  const loadActivityTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const folderProjectTypeResp = await fetchFolderProjectType(masterActivity.item_type_id);
      
      if (
        folderProjectTypeResp &&
        folderProjectTypeResp.result === 'OK' &&
        folderProjectTypeResp.TYPE[0].ACTIVITYTEMPLATES &&
        folderProjectTypeResp.TYPE[0].ACTIVITYTEMPLATES.TEMPLATE.length > 0
      ) {
        const sub_activity_templates = folderProjectTypeResp.TYPE[0].ACTIVITYTEMPLATES.TEMPLATE as SubActivityTemplate[];
        setActivityTemplates(sub_activity_templates);
        
        // Auto-select if only one sub-template exists
        if (sub_activity_templates.length === 1) {
          setSelectedActivityType(sub_activity_templates[0].activityType);
          
          var sub_activity_template = await fetchActivityTemplateById(sub_activity_templates[0].activityTemplate);
          if (sub_activity_template.TEMPLATE.initialStatus) {
            setInitialStatus(sub_activity_template.TEMPLATE.initialStatus.toString());
          }
          if (sub_activity_template.TEMPLATE.form) {
            setSelectedActivityTemplateFormId(sub_activity_template.TEMPLATE.form);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching folder project type for activity template:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchActivityTemplateById, fetchFolderProjectType, masterActivity.item_type_id]);

  // Load master activity context
  const loadMasterActivityContext = useCallback(async () => {
    try {
      const response = await projectService.getContextPath2(
        new GetContextPathRequest(store.Server, store.SessionId, masterActivity.id)
      );

      if (response.result === 'OK') {
        setMasterActivityContext(response.CONTEXT.map( context => FPADataFactory.createFromContextItem2(context)));
      } else {
        setMasterActivityContext([]);
      }
    } catch (error) {
      console.error('Error loading master activity context:', error);
      setMasterActivityContext([]);
    }
  }, [projectService, store.Server, store.SessionId, masterActivity.id]);

  // Filter serviced items on the frontend based on search query
  const filteredServicedItemsList = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return servicedItemsList;
    }
    
    const query = debouncedSearchQuery.trim().toLowerCase();
    return servicedItemsList.filter(item => {
      const name = (item.name || '').toLowerCase();
      const refId = (item.refId || '').toLowerCase();
      const itemSerial = (item.itemSerial || '').toLowerCase();
      
      return name.includes(query) || refId.includes(query) || itemSerial.includes(query);
    });
  }, [servicedItemsList, debouncedSearchQuery]);

  // Initialize data on mount
  useEffect(() => {
    loadServicedItems();
    loadActivityTemplates();
    loadMasterActivityContext();
  }, []); // Empty dependency array to run only once on mount

  // Reload serviced items when context changes
  useEffect(() => {
    if (masterActivityContext.length > 0) {
      loadServicedItems();
    }
  }, [selectedContext, masterActivityContext.length, loadServicedItems]);

  // Handle activity type change - fetches initial status for selected type
  const handleActivityTypeChange = useCallback(async (activityTypeId: number) => {
    setSelectedActivityType(activityTypeId);
    
    // Find the selected template
    const selectedTemplate = activityTemplates.find(template => template.activityType === activityTypeId);
    
    if (selectedTemplate) {
      try {
        const activityTemplate = await fetchActivityTemplateById(selectedTemplate.activityTemplate);
        if (activityTemplate.TEMPLATE.initialStatus) {
          setInitialStatus(activityTemplate.TEMPLATE.initialStatus.toString());
        } else {
          setInitialStatus(undefined);
        }
        if (activityTemplate.TEMPLATE.form) {
          setSelectedActivityTemplateFormId(activityTemplate.TEMPLATE.form);
        } else {
          setSelectedActivityTemplateFormId(undefined);
        }
      } catch (error) {
        console.error('Error fetching activity template for initial status:', error);
        setInitialStatus(undefined);
        setSelectedActivityTemplateFormId(undefined);
      }
    }
  }, [activityTemplates, fetchActivityTemplateById]);

  // Get selected serviced items
  const getSelectedServicedItems = useCallback((): ProjectItem[] => {
    return servicedItemsList.filter(item => selectedServicedItemKeys.has(item.id!));
  }, [servicedItemsList, selectedServicedItemKeys]);

  // Fetch direct children of a project item by parentId
  const fetchItemChildren = useCallback(async (parentItemId: string): Promise<ProjectItem[]> => {
    try {
      const response = await itemService.listProjectItem(
        new ListProjectItemRequest(store.Server, store.SessionId, 100, 0, {
          FILTER: {
            COL: [{
              name: 'parentId',
              type: 'equals',
              value: parentItemId
            }]
          }
        })
      );

      if (response.result === 'OK' && response.ROWS && response.ROWS.length > 0) {
        return response.ROWS.map(row => row.ITEM);
      }
      return [];
    } catch (error) {
      console.error(`Error fetching children for item ${parentItemId}:`, error);
      return [];
    }
  }, [itemService, store.Server, store.SessionId]);

  // Create a serviced item (type=4) from a source ProjectItem
  const createServicedItemFromSource = useCallback(async (
    sourceItem: ProjectItem,
    activityId: string,
    targetParentId?: string
  ): Promise<string | null> => {
    try {
      const newItem: ProjectItem = {
        name: sourceItem.name,
        item: sourceItem.item,
        itemSerial: sourceItem.itemSerial,
        quantity: sourceItem.quantity,
        totalQuantity: sourceItem.totalQuantity,
        amount: sourceItem.amount,
        catalogPrice: sourceItem.catalogPrice,
        totalAmount: sourceItem.totalAmount,
        totalAmountWithVat: sourceItem.totalAmountWithVat,
        currency: sourceItem.currency,
        purchaseCurrency: sourceItem.purchaseCurrency,
        vatValue: sourceItem.vatValue,
        purchasePrice: sourceItem.purchasePrice,
        attrib0: sourceItem.attrib0,
        attrib1: sourceItem.attrib1,
        attrib2: sourceItem.attrib2,
        supplier: sourceItem.supplier,
        refId: sourceItem.refId,
        note: sourceItem.note,
        folder: sourceItem.folder,
        project: sourceItem.project,
        activity: activityId,
        parentId: targetParentId,
        type: '4' // Serviced Items type
      };

      const response = await itemService.createProjectItem(
        new CreateProjectItemRequest(store.Server, store.SessionId, newItem)
      );

      if (response.result === 'OK' && response.itemId) {
        return response.itemId;
      }
      return null;
    } catch (error) {
      console.error('Error creating serviced item from source:', error);
      return null;
    }
  }, [itemService, store.Server, store.SessionId]);

  // Validate form
  const validateForm = useCallback((): { isValid: boolean; errorMessage?: string } => {
    if (!name.trim()) {
      return { isValid: false, errorMessage: _t('name_is_required') };
    }

    if (selectedServicedItemKeys.size === 0) {
      return { isValid: false, errorMessage: _t('select_at_least_one_serviced_item') };
    }

    if (activityTemplates.length > 1 && !selectedActivityType) {
      return { isValid: false, errorMessage: _t('activity_type_is_required') };
    }

    return { isValid: true };
  }, [name, selectedServicedItemKeys.size, activityTemplates.length, selectedActivityType]);

  // Create multiple activities
  const createActivities = useCallback(async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      ToastQueue.negative(validation.errorMessage || '', { timeout: 3000 });
      return;
    }

    setSaving(true);
    const selectedItems = getSelectedServicedItems();
    let successCount = 0;
    let errorCount = 0;
    let itemsTransferredCount = 0;
    let itemsFailedCount = 0;

    try {
      for (const item of selectedItems) {
        try {
          const activity: Activity = {
            name: name,
            startDate: dateFrom ? new Date(dateFrom.year, dateFrom.month - 1, dateFrom.day) : undefined,
            finalDate: dateTo ? new Date(dateTo.year, dateTo.month - 1, dateTo.day) : undefined,
            activityType: selectedActivityType?.toString(),
            folder: item.folder,
            project: item.project,
            masterActivity: masterActivity.id.toString(),
            refId: item.refId,
            customState: initialStatus,
            formId: selectedActivityTemplateFormId || '',
          };

          const response = await projectService.createActivity(
            new CreateActivityRequest(store.Server, store.SessionId, activity)
          );

          if (response.result === 'OK' && response.newId) {
            successCount++;
            const newActivityId = response.newId.toString();

            // Transfer the selected item to the newly created activity
            try {
              const createdItemId = await createServicedItemFromSource(item, newActivityId);
              
              if (createdItemId) {
                itemsTransferredCount++;
                
                // Fetch and transfer child items
                const children = await fetchItemChildren(item.id!);
                
                for (const child of children) {
                  try {
                    const childCreatedId = await createServicedItemFromSource(child, newActivityId, createdItemId);
                    if (childCreatedId) {
                      itemsTransferredCount++;
                    } else {
                      itemsFailedCount++;
                    }
                  } catch (error) {
                    itemsFailedCount++;
                    console.error(`Error transferring child item ${child.name}:`, error);
                  }
                }
              } else {
                itemsFailedCount++;
                console.error(`Failed to transfer item ${item.name} to activity ${newActivityId}`);
              }
            } catch (error) {
              itemsFailedCount++;
              console.error(`Error transferring items to activity ${newActivityId}:`, error);
            }
          } else {
            errorCount++;
            console.error(`Failed to create activity for item ${item.name}:`, response);
          }
        } catch (error) {
          errorCount++;
          console.error(`Error creating activity for item ${item.name}:`, error);
        }
      }

      // Show summary toast
      if (successCount > 0) {
        ToastQueue.positive(
          _t('activities_created_successfully', { count: successCount }),
          { timeout: 3000 }
        );
      }

      if (errorCount > 0) {
        ToastQueue.negative(
          _t('activities_creation_failed', { count: errorCount }),
          { timeout: 3000 }
        );
      }

      if (itemsTransferredCount > 0) {
        ToastQueue.positive(
          _t('items_transferred_successfully', { count: itemsTransferredCount }),
          { timeout: 3000 }
        );
      }

      if (itemsFailedCount > 0) {
        ToastQueue.negative(
          _t('items_transfer_failed', { count: itemsFailedCount }),
          { timeout: 3000 }
        );
      }

      // Close dialog and reload if any activities were created
      if (successCount > 0) {
        onClose(true);
      }
    } catch (error) {
      console.error('Error in createActivities:', error);
      ToastQueue.negative(_t('error_creating_activities'), { timeout: 3000 });
    } finally {
      setSaving(false);
    }
  }, [
    validateForm,
    getSelectedServicedItems,
    name,
    dateFrom,
    dateTo,
    selectedActivityType,
    initialStatus,
    selectedActivityTemplateFormId,
    masterActivity.id,
    projectService,
    store.Server,
    store.SessionId,
    onClose,
    _t,
    createServicedItemFromSource,
    fetchItemChildren
  ]);

  return {
    name,
    setName,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    servicedItemsList: filteredServicedItemsList,
    selectedServicedItemKeys,
    setSelectedServicedItemKeys,
    activityTemplates,
    selectedActivityType,
    setSelectedActivityType,
    handleActivityTypeChange,
    masterActivityContext,
    loading,
    saving,
    createActivities,
    validateForm,
    searchQuery,
    setSearchQuery,
  };
}

